import { matchTrackerApi, metricsServerApi } from '@frontend/core'
import { removeOneEvent } from '@frontend/core/lib/domains/events'
import { useLazyGetMatchStateQuery } from '@frontend/core/lib/domains/match'
import { ReactNode, useCallback, useEffect, useRef } from 'react'
import { useLocalMQTTClient } from '../modules/mqtt/hooks'
import { useAppDispatch } from '../store/hooks'
import { createFetchThrottle } from './fetch-throttle'
import { useIsSuperAdmin } from './hooks'

interface SuperAdminOnlyProps {
  children: ReactNode
  fallback?: ReactNode
}

type UpdateAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'FETCH'

type UpdateType = MatchStateUpdateType | SessionUpdateType | SyncUpdateType

type SyncUpdateType =
  'teams' | 'people' | 'teampeople' | 'competitions' | 'venues' | 'pitches'

type MatchStateUpdateType =
  'match' | 'match-sides' | 'match-possession' | 'match-undo-timing'

type SessionUpdateType = 'session'

interface UIUpdate {
  id: string
  sessionId: string
  host: string
  action: UpdateAction
  type: UpdateType
  path: string
  url: string
}

/**
 * Component that only renders its children if the current user is a SportableSuperAdmin
 */
export function SuperAdminOnly({
  children,
  fallback = null
}: SuperAdminOnlyProps) {
  const isSuperAdmin = useIsSuperAdmin()
  if (!isSuperAdmin) return fallback
  return <>{children}</>
}

//TODO this is for the local app so I don't think we need auth
export const SportableData = () => {
  const localMqttClient = useLocalMQTTClient()
  const dispatch = useAppDispatch()

  const [getMatchState] = useLazyGetMatchStateQuery()

  const fetchThrottle = useRef(
    createFetchThrottle(
      (type) => {
        dispatch(
          matchTrackerApi.util.invalidateTags([
            { type: type as any, id: 'LIST' }
          ])
        )
      },
      20_000,
      2_000
    )
  )

  const handleUIUpdate = useCallback(
    (update: UIUpdate) => {
      if (update.action === 'UPDATE') {
        switch (update.type) {
          case 'match':
          case 'match-possession':
          case 'match-sides':
            getMatchState()
            break
          case 'match-undo-timing':
            dispatch(
              matchTrackerApi.util.invalidateTags([
                { type: 'sessionEvents', id: update.sessionId }
              ])
            )
            break
          case 'session':
            dispatch(
              matchTrackerApi.util.invalidateTags([{ type: 'sessionEvents' }])
            )
            dispatch(
              metricsServerApi.util.invalidateTags([{ type: 'activeSession' }])
            )
        }
      } else if (update.action === 'CREATE') {
        switch (update.type) {
          case 'session':
            dispatch(
              matchTrackerApi.util.invalidateTags([{ type: 'sessionEvents' }])
            )
            dispatch(
              metricsServerApi.util.invalidateTags([{ type: 'activeSession' }])
            )
        }
      } else if (update.action === 'FETCH') {
        fetchThrottle.current.handle(update.type)
      } else if (update.action === 'DELETE') {
        switch (update.type) {
          case 'match-undo-timing': {
            const timingId = update.id
            dispatch(removeOneEvent(timingId))

            dispatch(
              matchTrackerApi.util.invalidateTags([
                { type: 'sessionEvents', id: update.sessionId }
              ])
            )
            break
          }
          default:
            console.error('unsupported update type', update)
        }
      }
    },
    [getMatchState]
  )

  useEffect(() => {
    if (localMqttClient) {
      localMqttClient.subscribe('metrics_server/ui_updates', (update) => {
        handleUIUpdate(update)
      })
    }

    return () => {
      if (localMqttClient) {
        localMqttClient.unsubscribe('metrics_server/ui_updates')
      }
      fetchThrottle.current.cleanup()
    }
  }, [localMqttClient, handleUIUpdate])

  return null
}
